import * as React from "react";
import {ReactNode, useEffect, useState} from "react";
import '../../../../css/chat-sidebar.css';
import {createPortal} from "react-dom";
import {LoaderSpinner} from "../LoaderSpinner";
import {DataStore} from "../../service/DataStore";
import EmailIcon from '@mui/icons-material/Email';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import HouseIcon from '@mui/icons-material/House';
import TaskIcon from '@mui/icons-material/Task';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import EngineeringIcon from '@mui/icons-material/Engineering';
import PhoneEnabledIcon from '@mui/icons-material/PhoneEnabled';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChatIcon from '@mui/icons-material/Chat';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';

import Grid from "@mui/material/Grid";
import ChatDestinationSelect from "./DestinationSelect";
import ChatDestinationDetails from "./DestinationDetails";
import CloseButton from "../CloseButton";
import Typography from "@mui/material/Typography";
import {GoogleTracking} from "../../helper/GoogleTracking";

interface ChatProps {
    config: {
        automatic: boolean,
        greeting: string,
        id: number,
        action_submit: string,
        ajax_url: string,
    }
}

// Verwende nicht 0 wegen if-Abfragen
export enum ChatDestinations {
    Verkauf = 1,
    Vermietung = 2,
    Hausverwaltung = 3,
    Bauabwicklung = 4,
    Ankauf = 5,
    Allgemein = 6,
}

export enum ContactOptions {
    Chat = 'Chat',
    Email = 'Email',
    Phone = 'Phone',
    Calendar = 'Calendar',
}

interface ContactOption {
    icon: ReactNode;
    label: string
}

const contactOptionMapping: Record<ContactOptions, ContactOption> = {
    [ContactOptions.Chat]: {icon: <ChatIcon/>, label: 'Wir freuen uns auf Ihre Nachricht!'},
    [ContactOptions.Email]: {icon: <EmailIcon/>, label: 'Wir freuen uns auf Ihre E-Mail!'},
    [ContactOptions.Phone]: {icon: <PhoneEnabledIcon/>, label: 'Wir freuen uns auf Ihren Anruf!'},
    [ContactOptions.Calendar]: {icon: <CalendarTodayIcon/>, label: 'Wir freuen uns auf Ihre Terminanfrage!'},
};

export class ChatDestinationEntry {
    constructor(public destination: ChatDestinations, public name: string, public email: string,
                public phone: string, public icon: ReactNode, public calendarLink?: string) {
    }
}


export default function Chat(props: ChatProps) {

    const ds = new DataStore();

    const [showChat, setShowChat] = useState<boolean>(false);
    const [isSending, setIsSending] = useState<boolean>(false);
    const [isHidden, setIsHidden] = useState<boolean>(ds.retrieveData('chat-hidden') == 'yes');
    const [showSuccess, setShowSuccess] = useState<boolean>(false);

    const [email, setEmail] = useState<string>('');
    const [message, setMessage] = useState<string>('');
    const [emailError, setEmailError] = useState<string>('');
    const [messageError, setMessageError] = useState<string>('');

    const [showDestinationSelect, setShowDestinationSelect] = useState<boolean>(false);
    const [selectedDestination, setSelectedDestination] = useState<ChatDestinationEntry>(null);
    const [contactOption, setContactOption] = useState<ContactOptions>(null);

    useEffect(() => {
        const mainElement = document.getElementById('main');

        // Check if the element exists before adding the event listener
        if (mainElement) {
            mainElement.addEventListener('click', () => closeDestinationSelect());
        }

        // Clean up the event listener when the component unmounts
        return () => {
            if (mainElement) {
                mainElement.removeEventListener('click', () => closeDestinationSelect());
            }
        };
    }, []);


    /**
     * E-Mails matchen mit chat.php VALID_DESTINATION_EMAILS
     */
    const getDestinationEntries = () => {
        return [
            new ChatDestinationEntry(
                ChatDestinations.Verkauf,
                "Verkauf",
                "beratung@erl.de",
                "+49 991 370 60 80",
                <HouseIcon/>,
                'https://meet.erl.de/sreitberger'
            ),
            new ChatDestinationEntry(
                ChatDestinations.Vermietung,
                "Vermietung",
                "info@erl-vermietung.de",
                "+49 991 370 60 8882",
                <MapsHomeWorkIcon/>
            ),
            new ChatDestinationEntry(
                ChatDestinations.Hausverwaltung,
                "Hausverwaltung",
                "hv@erl.de",
                "+49 991 370 60 85",
                <TaskIcon/>
            ),
            new ChatDestinationEntry(
                ChatDestinations.Bauabwicklung,
                "Bauabwicklung",
                "bau@erl.de",
                "+49 991 370 60 44",
                <EngineeringIcon/>
            ),
            new ChatDestinationEntry(
                ChatDestinations.Ankauf,
                "Ankauf",
                "ankauf@erl.de",
                "+49 991 370 60 241",
                <LocalOfferIcon/>
            ),
            new ChatDestinationEntry(
                ChatDestinations.Allgemein,
                "Allgemein",
                "info@erl.de",
                "+49 991 370 600",
                <ContactSupportIcon/>
            )
        ];

    }


    /**
     *
     * @param showParam
     * @param forceReset
     */
    const showTheChat = (showParam: boolean, forceReset: boolean = false) => {
        let show = showParam;

        if (forceReset) {
            setSelectedDestination(null);
            setShowDestinationSelect(true);
        }

        if (!showParam) {
            setShowDestinationSelect(false);
            show = false;
        } else {
            if (showParam && !selectedDestination) {
                setShowDestinationSelect(true);
            } else if (show && selectedDestination) {
                show = true;
            }
        }

        setShowChat(show);
    }

    /**
     *
     * @param newContactOption
     */
    const showDestSelect = (newContactOption: ContactOptions = null) => {
        if (showDestinationSelect && newContactOption == contactOption) {
            closeDestinationSelect();
        } else {
            closeDestinationSelect();
            setTimeout(() => {
                setSelectedDestination(null);
                setContactOption(newContactOption);
                setShowDestinationSelect(true);
            }, contactOption ? 250 : 0);
        }
    }

    const closeDestinationSelect = () => {
        setShowDestinationSelect(false);
        setShowChat(false);
    }

    const handleDestinationSelection = (entryObj: ChatDestinationEntry, contactOption: string) => {
        setSelectedDestination({...entryObj});

        if (contactOption === ContactOptions.Chat) {
            setShowChat(true);
            setShowDestinationSelect(false);
        }

        const gt = new GoogleTracking();
        gt.track('contactbar-' + entryObj.name.toLowerCase(), contactOption.toLowerCase() + '-view');
    }

    const hideChat = () => {
        ds.storeData('chat-hidden', 'yes');
        setIsHidden(true);
        closeDestinationSelect();
    }

    const showHiddenChat = () => {
        ds.storeData('chat-hidden', 'no');
        setIsHidden(false);
    }

    const closeSuccess = () => {
        setShowSuccess(false);
        setMessage('');
        setMessageError('');
        setEmailError('');
        showTheChat(false);
        closeDestinationSelect();
    }

    const submit = (selectedDestination: ChatDestinationEntry) => {
        setIsSending(true);
        setEmailError('');

        let formData = new FormData()
        formData.append("action", props.config.action_submit);
        formData.append("message", message);
        formData.append("email", email);
        formData.append("current_target_url", window.location.href);
        formData.append("id", props.config.id.toString());
        formData.append("destination_email", selectedDestination.email);

        fetch(props.config.ajax_url, {
            method: 'POST',
            body: formData,
        })
            .then(res => res.json())
            .then((apiResponse: any) => {

                if (apiResponse.success) {
                    setShowSuccess(true);
                    setIsSending(false);
                } else {
                    if (apiResponse.errors && apiResponse.errors.email) {
                        setEmailError(apiResponse.errors.email);
                    }

                    if (apiResponse.errors && apiResponse.errors.message) {
                        setMessageError(apiResponse.errors.message);
                    }
                }
                setIsSending(false);
            })
            .catch(reason => {
                setIsSending(false);
            });
    }

    const getDestinationsOptions = () => {
        return <>
            <div className={"dest-select-window " + (showDestinationSelect ? "show-dest-select-window" : "")}>

                {contactOption &&
                    <Grid container direction={"column"}>
                        <Grid container alignItems={"center"} spacing={2} wrap={"nowrap"} mb={2} pt={1}>
                            <Grid item display={"inherit"}>
                                {contactOptionMapping[contactOption].icon}
                            </Grid>
                            <Grid item>
                                <h3>{contactOptionMapping[contactOption].label}</h3>
                            </Grid>
                            <Grid item>
                                <CloseButton style={{color: 'white', marginTop: '-40px'}} onClick={() => closeDestinationSelect()}/>
                            </Grid>
                        </Grid>

                        {!selectedDestination &&
                            <ChatDestinationSelect contactOption={contactOption} destinationEntries={getDestinationEntries()}
                                                   handleDestinationSelection={handleDestinationSelection}/>
                        }

                        {selectedDestination &&
                            <ChatDestinationDetails contactOption={contactOption} selectedDestination={selectedDestination}
                                                    sendMessage={(destination) => handleDestinationSelection(destination, ContactOptions.Chat)}
                                                    handleDestinationBack={() => setSelectedDestination(null)}/>
                        }

                    </Grid>
                }
            </div>
        </>
    }

    return createPortal(
        <div className={"erl-chat"}>

            <div className={showChat ? "chat-background" : ""}></div>

            {getDestinationsOptions()}

            <div className={"chat-sidebar " + (isHidden ? 'chat-hidden' : '')}
                 onClick={() => {
                     isHidden && showHiddenChat();
                 }}>
                {selectedDestination &&
                    <>
                        <div className={"chat-window " + (showChat ? "show-chat-window" : "")}>
                            <div className="chat-header">
                                <CloseButton style={{color: 'white', float: 'right'}} onClick={() => showTheChat(false, true)}/>
                                <span className="consultant">Kontakt per E-Mail</span>
                            </div>

                            <div className={"chat-content"}>

                                <Typography>{props.config.greeting}</Typography>

                                {showSuccess &&
                                    <div className="chat-success">
                                        <h3>Vielen Dank für Ihre Nachricht</h3>
                                        <p>Wir werden uns umgehend bei Ihnen melden.</p>
                                        <p>Ihre Familie Erl.</p>

                                        <button className="btn btn-active white"
                                                onClick={() => closeSuccess()}>Schließen
                                        </button>
                                    </div>
                                }

                                {!showSuccess &&
                                    <>
                                        <div className="cont_input">
                                            <input type="email" placeholder="Ihre E-Mail Adresse" value={email}
                                                   onChange={(e) => setEmail(e.target.value)}/>
                                            <div className="error">{emailError}</div>
                                        </div>


                                        <div className="cont_textarea">
                                    <textarea placeholder="Ihre Nachricht an uns" value={message}
                                              onChange={(e) => setMessage(e.target.value)}/>
                                            <div className="error">{messageError}</div>

                                        </div>

                                        {!isSending &&
                                            <button className="btn btn-active white"
                                                    onClick={() => submit(selectedDestination)}>Weiter</button>
                                        }

                                        {isSending &&
                                            <LoaderSpinner/>
                                        }
                                    </>
                                }

                            </div>
                        </div>
                    </>
                }
                <div className={"chat-icons"}>
                    {Object.values(ContactOptions).map((option) => {
                        return <div key={option} className={"chat-icon"} onClick={() => showDestSelect(ContactOptions[option])}>
                            {contactOptionMapping[option].icon}
                        </div>
                    })}

                    <div className={"chat-icon"} onClick={() => hideChat()}>
                        <KeyboardArrowRightIcon className={"right-icon"}/>
                        <KeyboardArrowLeftIcon className={"left-icon"}/>
                    </div>
                </div>
            </div>
        </div>
        ,
        document.getElementById('top')
    );
}